// src/components/trip/PhotosTab.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Button,
  Flex,
  Text,
  Alert,
  SelectField
} from '@aws-amplify/ui-react';
import { Upload, Filter } from 'lucide-react';
import VirtualizedPhotoGrid from './VirtualizedPhotoGrid';
import PhotoViewer from './PhotoViewer';
import PhotoUploadOverlay from './PhotoUploadOverlay';
import { deletePhotos } from '../../services/tripPhotoActions';
import {
  loadVisibleAndNearbyPhotos,
  loadPhotoUrl,
  loadPhotoThumbnailUrl
} from '../../services/enhancedPhotoUrlService';
import { TripRole } from '../../API';
import { isErrored } from 'stream';

interface PhotosTabProps {
  photos: any[];
  tripId: string;
  userRole: string | null;
  photoUrls: { [key: string]: string };
  thumbnailUrls: { [key: string]: string };
  onPhotoUploaded: (newPhoto: any) => void;
  onPhotoDeleted: (photoIds: string[]) => void;
  onLoadPhotoUrl: (photoId: string, url: string, isThumbnail?: boolean) => void;
  onStorageLimitExceeded?: () => void;
}

const PhotosTab: React.FC<PhotosTabProps> = ({
  photos,
  tripId,
  userRole,
  photoUrls,
  thumbnailUrls,
  onPhotoUploaded,
  onPhotoDeleted,
  onLoadPhotoUrl,
  onStorageLimitExceeded
}) => {
  // State for selection mode
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);

  // State for photo viewer
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  // Upload overlay state
  const [showUploadOverlay, setShowUploadOverlay] = useState<boolean>(false);

  // Track visible photos
  const [visiblePhotoIds, setVisiblePhotoIds] = useState<string[]>([]);
  const lastProcessedVisibleIdsRef = useRef<string | null>(null);

  // User filter state
  const [selectedUploader, setSelectedUploader] = useState<string>('all');
  const [filteredPhotos, setFilteredPhotos] = useState<any[]>(photos);

  // Check if user can upload (must be a PARTICIPANT)
  const canUpload = userRole === TripRole.PARTICIPANT || userRole === 'CREATOR';
  const canEdit = userRole === TripRole.PARTICIPANT || userRole === 'CREATOR';

  // Extract unique uploaders from photos
  const uploaders = React.useMemo(() => {
    const uploadersMap = new Map<string, { id: string, name: string, count: number }>();

    photos.forEach(photo => {
      if (!uploadersMap.has(photo.uploadedBy)) {
        uploadersMap.set(photo.uploadedBy, {
          id: photo.uploadedBy,
          name: photo.uploaderName || 'Unknown User',
          count: 1
        });
      } else {
        const uploader = uploadersMap.get(photo.uploadedBy)!;
        uploadersMap.set(photo.uploadedBy, {
          ...uploader,
          count: uploader.count + 1
        });
      }
    });

    return Array.from(uploadersMap.values());
  }, [photos]);

  // Apply filter when selected uploader changes
  useEffect(() => {
    if (selectedUploader === 'all') {
      setFilteredPhotos(photos);
    } else {
      setFilteredPhotos(photos.filter(photo => photo.uploadedBy === selectedUploader));
    }
    // Reset selection when filter changes
    setSelectedPhotos(new Set());
  }, [selectedUploader, photos]);

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedPhotos(new Set());
  };

  // Toggle photo selection
  const togglePhotoSelection = (photoId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click

    setSelectedPhotos(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(photoId)) {
        newSelected.delete(photoId);
      } else {
        newSelected.add(photoId);
      }
      return newSelected;
    });
  };

  // Delete selected photos
  const deleteSelectedPhotos = async () => {
    if (selectedPhotos.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedPhotos.size} photo${selectedPhotos.size > 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      // Convert Set to Array before deleting
      const photoIdsToDelete = Array.from(selectedPhotos);
      const { successful, failed } = await deletePhotos(photoIdsToDelete);

      if (failed.length > 0) {
        setDeleteError(`Failed to delete ${failed.length} photos. Please try again.`);
      }

      if (successful.length > 0) {
        setDeleteSuccess(true);
        onPhotoDeleted(successful);
      }

      setSelectedPhotos(new Set());
      setSelectionMode(false);
    } catch (error) {
      console.error('Error deleting photos:', error);
      setDeleteError('Failed to delete one or more photos. Please try again.');
    } finally {
      setIsDeleting(false);

      // Clear success message after 3 seconds
      if (deleteSuccess) {
        setTimeout(() => setDeleteSuccess(false), 3000);
      }
    }
  };

  // Handle photo card click
  const handlePhotoClick = (photoId: string, index: number) => {
    if (selectionMode) {
      togglePhotoSelection(photoId, { stopPropagation: () => { } } as React.MouseEvent);
    } else {
      // Find the true index in the unfiltered photos array for proper navigation
      const photoInAllPhotos = photos.findIndex(p => p.id === photoId);
      setCurrentPhotoIndex(photoInAllPhotos);
      setViewerOpen(true);
    }
  };

  // Navigate between photos in the viewer
  const navigatePhotos = (direction: number) => {
    setCurrentPhotoIndex(prevIndex => {
      let newIndex = prevIndex + direction;

      // Handle wrapping around
      if (newIndex < 0) {
        newIndex = photos.length - 1;
      } else if (newIndex >= photos.length) {
        newIndex = 0;
      }

      return newIndex;
    });
  };

  // Request to load a photo URL (e.g., for the viewer)
  const requestLoadPhotoUrl = async (photoId: string, thumbnailOnly: boolean = true) => {
    try {
      // Determine which cache to check
      const cache = thumbnailOnly ? thumbnailUrls : photoUrls;

      // Check if already loaded
      if (cache[photoId]) {
        return cache[photoId];
      }

      // Load with high priority
      const url = thumbnailOnly
        ? await loadPhotoThumbnailUrl(photoId, true)
        : await loadPhotoUrl(photoId, false, true);

      // Update parent component
      onLoadPhotoUrl(photoId, url, thumbnailOnly);

      return url;
    } catch (error) {
      console.error(`Error loading photo URL for ${photoId}:`, error);
      return null;
    }
  };

  // Handle visible photos change - optimized for the enhanced photo service
  const handleVisiblePhotosChange = useCallback(async (visibleIds: string[]) => {

    const visibleIdsKey = JSON.stringify(visibleIds.sort());
    if (lastProcessedVisibleIdsRef.current === visibleIdsKey) {
      return; // Skip if the same photos are still visible
    }
    lastProcessedVisibleIdsRef.current = visibleIdsKey;
    // Update state with the currently visible photos
    setVisiblePhotoIds(prevIds => {
      // If the same photos are visible, don't update state
      if (prevIds.length === visibleIds.length &&
        prevIds.every(id => visibleIds.includes(id))) {
        return prevIds;
      }
      return visibleIds;
    });

    // Use the enhanced service to load visible photos and queue nearby ones
    try {
      // Get all photo IDs
      const allPhotoIds = photos.map(photo => photo.id);

      // Load visible photos with thumbnails and queue preloading for nearby ones
      const loadedUrls = await loadVisibleAndNearbyPhotos(visibleIds, allPhotoIds, 15, true);

      // Update parent with any new URLs
      Object.entries(loadedUrls).forEach(([photoId, url]) => {
        onLoadPhotoUrl(photoId, url, true);
      });
    } catch (error) {
      console.error('Error loading visible photos:', error);
    }
  }, [photos, onLoadPhotoUrl]);

  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUploader(e.target.value);
  };

  // Reset filter
  const resetFilter = () => {
    setSelectedUploader('all');
  };

  return (
    <View>
      {/* Photo management controls - Simple right-aligned design */}
      <Flex
        direction={{ base: 'column', medium: 'row' }}
        justifyContent="flex-end"
        alignItems={{ base: 'stretch', medium: 'center' }}
        gap="1rem"
        marginBottom="1.5rem"
      >
        {/* Filter - First control */}
        {photos.length > 0 && (
          <Flex
            alignItems="center"
            gap="0.5rem"
            order={{ base: 3, medium: 1 }}
          >
            <Filter
              size={16}
              style={{ color: selectedUploader !== 'all' ? '#3B82F6' : '#AAAAAA' }}
            />
            <SelectField
              label=""
              labelHidden={true}
              value={selectedUploader}
              onChange={handleFilterChange}
              backgroundColor="#1A1A1A"
              borderColor="#303030"
              color="white"
              width={{ base: '100%', medium: 'auto' }}
            >
              <option value="all">All Photos ({photos.length})</option>
              {uploaders.map(uploader => (
                <option key={uploader.id} value={uploader.id}>
                  {uploader.name} ({uploader.count})
                </option>
              ))}
            </SelectField>
            {selectedUploader !== 'all' && (
              <Button variation="link" size="small" onClick={resetFilter}>
                Clear
              </Button>
            )}
          </Flex>
        )}

        {/* Select Photos - Second control */}
        {canEdit && photos.length > 0 && (
          <Button
            variation={selectionMode ? "primary" : "destructive"}
            onClick={toggleSelectionMode}
            size="small"
            order={{ base: 2, medium: 2 }}
          >
            {selectionMode ? "Cancel Selection" : "Select Photos"}
          </Button>
        )}

        {/* Upload Button - Third control */}
        {canUpload && (
          <Button
            onClick={() => setShowUploadOverlay(true)}
            variation="primary"
            size="small"
            order={{ base: 1, medium: 3 }}
          >
            <Flex alignItems="center" gap="0.5rem">
              <Upload size={16} />
              <span>Upload Photos</span>
            </Flex>
          </Button>
        )}

        {/* Delete Selected - Appears when in selection mode */}
        {selectionMode && (
          <Button
            variation="destructive"
            onClick={deleteSelectedPhotos}
            isLoading={isDeleting}
            isDisabled={selectedPhotos.size === 0}
            size="small"
            order={{ base: 4, medium: 4 }}
          >
            Delete Selected ({selectedPhotos.size})
          </Button>
        )}
      </Flex>

      {/* Status messages */}
      {deleteError && (
        <Alert
          variation="error"
          isDismissible={true}
          onDismiss={() => setDeleteError(null)}
          marginBottom="1rem"
        >
          {deleteError}
        </Alert>
      )}

      {deleteSuccess && (
        <Alert
          variation="success"
          isDismissible={true}
          onDismiss={() => setDeleteSuccess(false)}
          marginBottom="1rem"
        >
          Successfully deleted {selectedPhotos.size > 0 ? selectedPhotos.size : ''} photo(s).
        </Alert>
      )}

      {/* Photo Grid */}
      {filteredPhotos.length === 0 ? (
        <View textAlign="center" padding="3rem" backgroundColor="#1A1A1A" borderRadius="medium">
          <Text fontSize="1.2rem" color="white">
            {photos.length === 0
              ? "No photos in this trip yet."
              : "No photos match the current filter."}
          </Text>
          {photos.length > 0 && selectedUploader !== 'all' && (
            <Button marginTop="1rem" variation="link" onClick={resetFilter}>
              Clear Filter
            </Button>
          )}
          {canUpload && photos.length === 0 && (
            <Text marginTop="0.5rem" color="#AAAAAA">Use the Upload Photos button to add photos.</Text>
          )}
        </View>
      ) : (
        <VirtualizedPhotoGrid
          photos={filteredPhotos}
          isSelectionMode={selectionMode}
          selectedPhotos={selectedPhotos}
          photoUrls={photoUrls}
          thumbnailUrls={thumbnailUrls}
          onPhotoSelect={togglePhotoSelection}
          onPhotoClick={handlePhotoClick}
          onVisiblePhotosChange={handleVisiblePhotosChange}
          userRole={userRole}
        />
      )}

      {/* Photo Viewer - always uses the full photo list for navigation */}
      <PhotoViewer
        isOpen={viewerOpen}
        photos={photos}
        currentIndex={currentPhotoIndex}
        onClose={() => setViewerOpen(false)}
        onNavigate={navigatePhotos}
        userRole={userRole}
      />

      {/* Upload Overlay */}
      <PhotoUploadOverlay
        isOpen={showUploadOverlay}
        tripId={tripId}
        onClose={() => setShowUploadOverlay(false)}
        onPhotoUploaded={onPhotoUploaded}
        userRole={userRole}
        onStorageLimitExceeded={onStorageLimitExceeded} // Pass it down
      />
    </View>
  );
};

export default PhotosTab;